<template>
  <div class="search-select-container">
    <!-- search input -->
    <input
      :ref="inputId"
      v-model="entered"
      type="text"
      :class="{ [inputClass]: true, error: !isValid }"
      @focus="handleInputFocus"
      @blur="handleInputBlur"
      @keydown.arrow-down="selectFirstOptionByDown"
      @keydown.arrow-up="selectLastOptionByUp"
    />
    <!-- dropdown animation component -->
    <transition name="search-tab">
      <!-- search option -->
      <div v-if="searchTabShow" :ref="searchTabId" class="search-results-tab">
        <button
          v-for="opt in filteredOptions"
          :key="opt"
          :class="optionClass"
          :value="opt"
          @click="approveSelect(opt)"
          @keydown.enter="approveSelect(opt)"
          @blur="handleOptionBlur"
          @keydown.arrow-down="selectNextOptionByDown"
          @keydown.arrow-up="selectPreviousOptionByUp"
        >
          {{ opt }}
        </button>

        <div v-if="filteredOptions.length == 0">
          No searched results
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
// Select with dynamic search dropdown
export default {
  name: "SearchSelect",
  props: {
    // select options
    options: {
      type: Array,
      required: true
    },
    // initial select value
    value: {
      type: String,
    }
  },
  data() {
    return {
      /** input text */
      entered: null,

      /** to show options dropdown */
      searchTabShow: false,
      /** is input in options */
      isValid: true,
      selectedOptionIndex: -1,

      optionClass: "search-result-option",
      inputClass: "search-select-input"
    };
  },
  computed: {
    // @vuese
    // Input element HTML id
    inputId() {
      return `${this._uid}_search-select`;
    },
    // @vuese
    // Searching dropdown HTML id
    searchTabId() {
      return `${this._uid}_tab`;
    },
    // @vuese
    // Options starting from current entered value
    filteredOptions() {
      return this.options.filter(
        opt => opt.toLowerCase().search(this.entered.toLowerCase()) >= 0
      );
    }
  },
  watch: {
    // @vuese
    // Update local input value when external is changed
    value() {
      this.syncEntered();
    },
    // @vuese
    // Clear error state when user start to type
    entered() {
      this.isValid = true;
    }
  },
  mounted() {
    this.syncEntered();
  },
  methods: {
    // @vuese
    // Set local input text as same to external value if it's in available options
    syncEntered() {
      if (this.checkOptionExists(this.value)) {
        this.entered = this.value;
      } else {
        this.entered = this.options[0];
      }
    },
    // @vuese
    // Open search tab when user focused on input
    handleInputFocus() {
      /** open search tab */
      this.searchTabShow = true;
      this.selectedOptionIndex = -1;
    },
    // @vuese
    // Handle input blur event by two ways: <br>
    // 1. User clicked on search dropdown option, then update selected index <br>
    // 2. User clicked out from select, then hide dropdown
    handleInputBlur({ relatedTarget }) {
      if (relatedTarget?.classList[0] == this.optionClass) {
        /** update selected option index */
        const index = this.filteredOptions.findIndex(
          opt => opt == relatedTarget.value
        );
        this.selectedOptionIndex = index;
      } else {
        /** check input and close search tab */
        this.validate();
        this.searchTabShow = false;
      }
    },
    // @vuese
    // Enter selected option into input and check that it's valid
    // @arg option - selected option
    approveSelect(option) {
      /** set current input text */
      this.entered = option;
      /** unfocus option */
      this.searchTabShow = false;
      /** validation */
      this.validate();
    },
    // @vuese
    // Handle option blur event by three ways: <br>
    // 1. User clicked on input or another option, then update selected index <br>
    // 2. User clicked out from select, then hide dropdown
    handleOptionBlur({ relatedTarget }) {
      if (relatedTarget?.classList[0] == this.inputClass) {
        this.selectedOptionIndex = -1;
      } else if (relatedTarget?.classList[0] == this.optionClass) {
        const index = this.filteredOptions.findIndex(
          opt => opt == relatedTarget.value
        );
        this.selectedOptionIndex = index;
      } else {
        this.searchTabShow = false;
        this.selectedOptionIndex = -1;
      }
    },
    // @vuese
    // Focus next option in search
    selectNextOption() {
      if (this.selectedOptionIndex < 0) {
        return;
      }

      let nextIndex = this.selectedOptionIndex + 1;
      nextIndex %= this.filteredOptions.length;
      this.selectOption(nextIndex);
    },
    // @vuese
    // Focus previous option in search
    selectPreviousOption() {
      if (this.selectedOptionIndex < 0) {
        return;
      }

      if (this.selectedOptionIndex == 0) {
        this.selectOption(this.filteredOptions.length - 1);
      } else {
        const prevIndex = this.selectedOptionIndex - 1;
        this.selectOption(prevIndex);
      }
    },
    // @vuese
    // Handle arrow-down click to select first option
    selectFirstOptionByDown(e) {
      this.selectOption(0);
      // prevent list scrolling by arrow
      e.preventDefault();
    },
    // @vuese
    // Handle arrow-down click to select last option
    selectLastOptionByUp(e) {
      this.selectOption(this.filteredOptions.length - 1);
      // prevent list scrolling by arrow
      e.preventDefault();
    },
    // @vuese
    // Handle arrow-down click to select next option
    selectNextOptionByDown(e) {
      this.selectNextOption();
      // prevent list scrolling by arrow
      e.preventDefault();
    },
    // @vuese
    // Handle arrow-up click to select previous option
    selectPreviousOptionByUp(e) {
      this.selectPreviousOption();
      // prevent list scrolling by arrow
      e.preventDefault();
    },
    // @vuese
    // Focus option
    // @arg index - option's index in searh list
    selectOption(index) {
      const optionsTab = this.$refs[this.searchTabId];
      const options = optionsTab.getElementsByClassName(this.optionClass);
      const targetOption = options[index];

      targetOption.focus();
    },
    // @vuese
    // Update input validation status
    validate() {
      if (this.checkOptionExists(this.entered)) {
        this.$emit("change", this.entered);
      } else {
        this.$emit("error");
        this.isValid = false;
      }
    },
    // @vuese
    // Is option in available options list
    // @arg opption - option's name
    checkOptionExists(option) {
      return this.options.findIndex(opt => opt === option) >= 0;
    }
  }
};
</script>

<style scoped>
/** select styles */
.search-select-input {
  width: 300px;
  box-sizing: border-box;
  border-width: 1px;
  outline: none;

  padding: 0.25em;

  border-radius: 4px;
}

.search-select-input:focus {
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}

.search-select-input.error {
  border-color: red;
}

/** search options block styles */
.search-results-tab {
  position: fixed;
  box-sizing: border-box;

  /** display: none; */

  width: 300px;
  max-height: 200px;

  padding-bottom: 0.25em;
  padding-top: 0.25em;

  background-color: white;
  border: 1px solid black;

  border-bottom-left-radius: 5px;
  border-bottom-right-radius: 5px;

  overflow-x: hidden;
  overflow-y: auto;
}

/** transition styles */
.search-tab-enter,
.search-tab-leave-to {
  max-height: 0;
  opacity: 0;
}

.search-tab-enter-active,
.search-tab-leave-active {
  transition: max-height 0.5s, opacity 0.5s;
  overflow-y: hidden;
}

/** selected tab styles */
.search-result-option {
  width: 100%;

  padding: 0.25em;

  background-color: transparent;
  border: none;
  cursor: pointer;
}
.search-result-option:is(:hover, :focus-visible) {
  background-color: #ccc;
  border: none;
  outline: none;
}
</style>
